/**
 * QueueContext - Advanced Queue Management System
 * 
 * Features:
 * - Smart Window Assignment: Uses clustering algorithm to distribute students evenly
 *   across active windows that support the requested service
 * - Feedback System: Collects ratings and comments after service completion
 * - Real-time Updates: Automatically refreshes queue data every 5 seconds
 * - Service Requirements: Displays required documents for each service type
 */

import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { useAuth } from './AuthContext';
import { DatabaseErrorDialog } from '../components/DatabaseErrorDialog';

export type QueueStatus = 'waiting' | 'serving' | 'completed' | 'cancelled' | 'on_hold';
export type ServiceType = 'enrollment' | 'grades' | 'certification' | 'clearance' | 'other';

export interface QueueEntry {
  id: string;
  studentId: string;
  studentName: string;
  serviceType: ServiceType;
  serviceName: string;
  // Support for multiple services in a single queue entry
  services?: Array<{serviceType: ServiceType, serviceName: string}>;
  windowNumber: number;
  queueNumber: number;
  status: QueueStatus;
  joinedAt: string;
  servedAt?: string;
  completedAt?: string;
  feedbackRating?: number;
  feedbackComment?: string;
  splitFromBatch?: boolean; // Indicates this queue was auto-split from a batch request
  cancellationReason?: string;
  onHoldAt?: string;
  resumedAt?: string;
  totalHoldDuration?: number;
}

interface QueueContextType {
  queues: QueueEntry[];
  requestService: (studentId: string, studentName: string, serviceType: ServiceType, serviceName: string) => Promise<QueueEntry | null>;
  requestMultipleServices: (studentId: string, studentName: string, services: Array<{serviceType: ServiceType, serviceName: string}>) => Promise<{success: number, failed: number, errors?: string[], queueIds?: string[], windowNumbers?: number[]}>;
  updateQueueStatus: (queueId: string, status: QueueStatus, cancellationReason?: string) => Promise<boolean>;
  submitFeedback: (queueId: string, rating: number, comment: string) => Promise<void>;
  getStudentQueue: (studentId: string) => QueueEntry | undefined;
  getStudentQueues: (studentId: string) => QueueEntry[];
  getWindowQueue: (windowNumber: number) => QueueEntry[];
  getAllQueues: () => QueueEntry[];
  cancelQueue: (queueId: string, cancellationReason?: string) => Promise<void>;
  getNextInQueue: (windowNumber: number) => QueueEntry | undefined;
  deleteQueue: (queueId: string) => Promise<void>;
  refreshQueues: () => Promise<void>;
  loading: boolean;
}

const QueueContext = createContext<QueueContextType | undefined>(undefined);

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-d4ddf86c`;

export function QueueProvider({ children }: { children: ReactNode }) {
  const [queues, setQueues] = useState<QueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDbError, setShowDbError] = useState(false);
  const { accessToken } = useAuth();

  const refreshQueues = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(`${API_BASE}/queues`, {
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        setQueues(data.queues || []);
        setShowDbError(false); // Clear error if successful
      } else {
        console.error('Error fetching queues: Server returned', response.status);
        
        // Check if this is a database initialization error
        if (response.status === 500) {
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              console.log('Error response data:', errorData); // Debug log
              if (errorData.dbError || 
                  errorData.details?.includes('kv_store_d4ddf86c') || 
                  errorData.error?.includes('Database not initialized') ||
                  errorData.details?.includes('relation') ||
                  errorData.details?.includes('does not exist') ||
                  errorData.details?.includes('connection reset') ||
                  errorData.details?.includes('connection error') ||
                  errorData.initUrl) {
                console.log('Database error detected, showing dialog');
                setShowDbError(true);
              }
            } else {
              // Non-JSON response (likely HTML error page or network error)
              const errorText = await response.text();
              console.error('Non-JSON error response:', errorText.substring(0, 200));
              // Show DB error dialog for 500 errors even if not JSON
              setShowDbError(true);
            }
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            // If we can't parse the error but got a 500, assume it's a DB error
            setShowDbError(true);
          }
        } else if (response.status === 404) {
          // Server function not found - likely not deployed
          console.error('Server function not found (404). Please deploy the server function.');
          setShowDbError(true);
        }
      }
    } catch (error) {
      // Check if this might be a database error even in caught exceptions
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error fetching queues:', error.message);
        // If we're getting persistent errors, it might be a database issue
        if (error.message.includes('500') || 
            error.message.includes('Server') || 
            error.message.includes('Failed to fetch') ||
            error.message.includes('NetworkError')) {
          setShowDbError(true);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshQueues();
    // Refresh every 5 seconds for real-time updates
    const interval = setInterval(refreshQueues, 5000);
    return () => clearInterval(interval);
  }, [refreshQueues]);

  const requestService = async (studentId: string, studentName: string, serviceType: ServiceType, serviceName: string): Promise<QueueEntry | null> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(`${API_BASE}/queues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ studentId, studentName, serviceType, serviceName }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        await refreshQueues();
        return data.queue;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error requesting service:', errorData.error || response.statusText);
        alert(errorData.error || 'Unable to request service. Please try again.');
      }
      return null;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          alert('Request timed out. Please check your connection and try again.');
        } else {
          console.error('Error requesting service:', error.message);
          alert('Unable to connect to server. Please check the connection status and try again.');
        }
      }
      return null;
    }
  };

  const submitFeedback = async (queueId: string, rating: number, comment: string) => {
    try {
      const response = await fetch(`${API_BASE}/queues/${queueId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ rating, comment })
      });

      if (response.ok) {
        await refreshQueues();
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const updateQueueStatus = async (queueId: string, status: QueueStatus, cancellationReason?: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/queues/${queueId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ status, ...(cancellationReason && { cancellationReason }) })
      });

      if (response.ok) {
        await refreshQueues();
        return true;
      }
      console.error('Error updating queue: Server returned', response.status);
      return false;
    } catch (error) {
      console.error('Error updating queue:', error);
      return false;
    }
  };

  const cancelQueue = async (queueId: string, cancellationReason?: string) => {
    await updateQueueStatus(queueId, 'cancelled', cancellationReason);
  };

  const deleteQueue = async (queueId: string) => {
    try {
      // Optimistically remove the queue from local state immediately
      setQueues(prev => prev.filter(q => q.id !== queueId));
      
      const response = await fetch(`${API_BASE}/queues/${queueId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken || publicAnonKey}`
        }
      });

      if (response.ok) {
        await refreshQueues();
      }
    } catch (error) {
      console.error('Error deleting queue:', error);
      // Refresh to restore state if delete failed
      await refreshQueues();
    }
  };

  const requestMultipleServices = async (
    studentId: string, 
    studentName: string, 
    services: Array<{serviceType: ServiceType, serviceName: string}>
  ): Promise<{success: number, failed: number, errors?: string[], queueIds?: string[], windowNumbers?: number[]}> => {
    // If only one service, use the regular endpoint
    if (services.length === 1) {
      const result = await requestService(studentId, studentName, services[0].serviceType, services[0].serviceName);
      return result 
        ? { success: 1, failed: 0, queueIds: [result.id], windowNumbers: [result.windowNumber] }
        : { success: 0, failed: 1, errors: [`Failed to request ${services[0].serviceName}`] };
    }

    // For multiple services, use batch endpoint that assigns to a single window
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(`${API_BASE}/queues/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ studentId, studentName, services }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        await refreshQueues();
        
        // Check if services were split into multiple queues
        if (data.split && data.queues) {
          const successCount = data.queues.length;
          const failedCount = data.failedServices ? data.failedServices.length : 0;
          
          // Show informative message about split
          if (data.message) {
            const queueDetails = data.queues.map((q: any) => 
              `• ${q.serviceName} → Window ${q.windowNumber} (Queue #${q.queueNumber})`
            ).join('\n');
            
            alert(
              data.message + 
              '\n\nYour queue assignments:\n' + 
              queueDetails +
              (failedCount > 0 ? `\n\nFailed services: ${data.failedServices.join(', ')}` : '') +
              '\n\nPlease check your dashboard for details.'
            );
          }
          
          return { 
            success: successCount, 
            failed: failedCount,
            errors: failedCount > 0 ? data.failedServices : undefined,
            queueIds: data.queues.map((q: any) => q.id),
            windowNumbers: data.queues.map((q: any) => q.windowNumber)
          };
        }
        
        // Single unified queue was created
        return { success: 1, failed: 0, queueIds: [data.queue.id], windowNumbers: [data.queue.windowNumber] };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error requesting batch services:', errorData.error || response.statusText);
        alert(errorData.error || 'Unable to request services. Please try again.');
        return { success: 0, failed: 1, errors: [errorData.error || 'Batch request failed'] };
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          alert('Request timed out. Please check your connection and try again.');
        } else {
          console.error('Error requesting batch services:', error.message);
          alert('Unable to connect to server. Please check the connection status and try again.');
        }
      }
      return { success: 0, failed: 1, errors: ['Connection error'] };
    }
  };

  const getStudentQueue = (studentId: string): QueueEntry | undefined => {
    return queues.find(q => q.studentId === studentId && (q.status === 'waiting' || q.status === 'serving' || q.status === 'on_hold'));
  };

  const getStudentQueues = (studentId: string): QueueEntry[] => {
    return queues.filter(q => q.studentId === studentId && (q.status === 'waiting' || q.status === 'serving' || q.status === 'on_hold'));
  };

  const getWindowQueue = (windowNumber: number): QueueEntry[] => {
    return queues.filter(q => q.windowNumber === windowNumber).sort((a, b) => a.queueNumber - b.queueNumber);
  };

  const getAllQueues = (): QueueEntry[] => {
    return [...queues].sort((a, b) => new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime());
  };

  const getNextInQueue = (windowNumber: number): QueueEntry | undefined => {
    const waitingQueues = queues
      .filter(q => q.windowNumber === windowNumber && q.status === 'waiting')
      .sort((a, b) => new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime());
    return waitingQueues[0];
  };

  return (
    <QueueContext.Provider value={{
      queues,
      requestService,
      requestMultipleServices,
      updateQueueStatus,
      submitFeedback,
      getStudentQueue,
      getStudentQueues,
      getWindowQueue,
      getAllQueues,
      cancelQueue,
      getNextInQueue,
      deleteQueue,
      refreshQueues,
      loading
    }}>
      {children}
      <DatabaseErrorDialog 
        open={showDbError} 
        onClose={() => {
          setShowDbError(false);
          // Retry fetching after user claims to have set up the database
          refreshQueues();
        }} 
      />
    </QueueContext.Provider>
  );
}

export function useQueue() {
  const context = useContext(QueueContext);
  if (context === undefined) {
    throw new Error('useQueue must be used within a QueueProvider');
  }
  return context;
}